import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as turf from '@turf/turf';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry, MultiPolygon, Polygon } from 'geojson';
import * as L from 'leaflet';
import { LatLng } from 'leaflet';
import { Observable } from 'rxjs';
import { IReGroupByVmStatusIdAndFeatures, vmStatIdLookup } from 'src/app/models/shared-models';
import { ILotPlanRegionalEcosystemWithMetadata, SpatialConfigModel } from 'src/app/veg-wizard/models/veg-wizard-models';
import { environment } from 'src/environments/environment';
@Injectable({
  providedIn: 'root',
})
export class MapService {
  constructor(private http: HttpClient) {}

  unionFeatures(featureArray: Array<Feature<Polygon | MultiPolygon>>): Feature<Polygon | MultiPolygon> | null {
    if (featureArray.length === 0) {
      return null;
    }

    if (featureArray.length === 1) {
      return featureArray[0];
    }

    if (featureArray.length === 2) {
      const poly1 = turf.feature(featureArray[0].geometry);
      const poly2 = turf.feature(featureArray[1].geometry);
      const result = turf.union({
        type: 'FeatureCollection',
        features: [poly1, poly2],
      });
      return result as Feature<Polygon | MultiPolygon>;
    }

    const unionResult = featureArray.reduce((prev, next) => {
      const poly1 = turf.feature(prev.geometry);
      const poly2 = turf.feature(next.geometry);

      const fu = turf.union({
        type: 'FeatureCollection',
        features: [poly1, poly2],
      });
      return fu as Feature<Polygon | MultiPolygon>;
    });

    return unionResult;
  }

  combineFeatureCollection(featureArray: FeatureCollection<Polygon | MultiPolygon>): FeatureCollection<Polygon | MultiPolygon> {
    const result = turf.combine(featureArray);
    return result as FeatureCollection<Polygon | MultiPolygon>;
  }

  getLotPlanRegionalEcosystemData$(lot: string, plan: string): Observable<ILotPlanRegionalEcosystemWithMetadata[]> {
    const lotp = encodeURIComponent(lot);
    const planp = encodeURIComponent(plan);
    const url = `${environment.apiUrl}/spatialmap/get-lotplan-regional-ecosystem-with-metadata?lot=${lotp}&plan=${planp}`;

    return this.http.get<ILotPlanRegionalEcosystemWithMetadata[]>(url);
  }

  getLotPlanVegetationFeatureGeoJsonLayerData$(lot: string, plan: string, layerId: number): Observable<string> {
    const lotp = encodeURIComponent(lot);
    const planp = encodeURIComponent(plan);
    const url = `${environment.apiUrl}/spatialmap/get-lotplan-veg-feature-geojson?lot=${lotp}&plan=${planp}&layerId=${layerId}`;

    return this.http.get<string>(url);
  }

  getLotPlanBiotaVegetationFeatureGeoJsonLayerData$(lot: string, plan: string, layerId: number): Observable<string> {
    const lotp = encodeURIComponent(lot);
    const planp = encodeURIComponent(plan);
    const url = `${environment.apiUrl}/spatialmap/get-lotplan-biota-veg-feature-geojson?lot=${lotp}&plan=${planp}&layerId=${layerId}`;
    return this.http.get<string>(url);
  }

  getConfig(): SpatialConfigModel {
    const spatialServerRoot = environment.vegSpatialServerRootUrl;
    const addressTypeAheadURL = spatialServerRoot + environment.vegSpatialAddressTypeAheadURL;
    const addressURL = spatialServerRoot + environment.vegSpatialAddressURL;
    const lotplanMapURL = spatialServerRoot + environment.vegSpatialLotplanMapURL;
    const lotplanbupURL = spatialServerRoot + environment.vegSpatialLotplanbupURL;

    const configModel: SpatialConfigModel = {
      AddressTypeAheadURL: addressTypeAheadURL,
      AddressURL: addressURL,
      LotplanMapURL: lotplanMapURL,
      LotplanbupURL: lotplanbupURL,
    };

    return configModel;
  }

  getRandomColour(): string {
    return '#' + (0x1000000 + Math.random() * 0xffffff).toString(16).substring(0, 6);
  }

  calculateTotalArea(layers: L.Polyline[]): number {
    if (!layers || layers.length === 0) {
      return 0;
    }

    const latLongs = layers.map((layer) => L.GeometryUtil.geodesicArea(layer.getLatLngs()[0] as L.LatLngLiteral[]));

    const totalSizeInMetersSquared = latLongs.reduce((total, area) => total + area);
    const totalSizeInHectares = totalSizeInMetersSquared / 10000;

    return Number(totalSizeInHectares.toFixed(2));
  }

  calculateTotalLineLength(layers: L.Polyline[]): number {
    if (!layers || layers.length === 0) {
      return 0;
    }

    let distance = 0;

    for (let layer of layers) {
      if (layer instanceof L.Polyline) {
        const line = layer as L.Polyline;
        const sections: LatLng[] = line.getLatLngs() as LatLng[];

        for (let i = 0; i < sections.length - 1; i++) {
          distance += sections[i].distanceTo(sections[i + 1]);
        }
      }
    }

    return distance;
  }

  doAnyAreasOverlap(areas: Feature<Geometry, GeoJsonProperties>[]): boolean {
    // If there are less than 2 areas, they cannot overlap.
    if (!areas || areas.length < 2) {
      return false;
    }

    // Loop through every area, and check if it overlaps with any other area.
    for (let i = 0; i < areas.length; i++) {
      const currentArea = areas[i];

      for (let j = i + 1; j < areas.length; j++) {
        const otherArea = areas[j];

        if (this.areasAreOverlapping(currentArea, otherArea)) {
          return true;
        }
      }
    }

    return false;
  }

  areAreasInBounds(userCreatedAreas: any, lotPlanAreas: any): boolean {
    for (let j = 0; j < userCreatedAreas.length; j++) {
      let diff = turf.difference(turf.featureCollection([userCreatedAreas[j], lotPlanAreas[0]]));

      for (let i = 1; i < lotPlanAreas.length; i++) {
        if (diff == null) {
          continue;
        }

        diff = turf.difference(turf.featureCollection([diff, lotPlanAreas[i]]));

        if (diff === null) {
          break;
        }
      }

      if (diff === null) {
        continue;
      } else {
        return false;
      }
    }

    return true;
  }

  areLinesInBounds(userCreatedLines: any, lotPlanAreas: any): boolean {
    const lotPlanUnion = this.unionFeatures(lotPlanAreas);
    if (!lotPlanUnion) return true;

    for (const line of userCreatedLines) {
      if (!turf.booleanWithin(line, lotPlanUnion)) {
        return false;
      }
    }

    return true;
  }

  removeAlphabetCharactersAndConvertToArray(input: string): string[] {
    // replace any alphabet characters with empty string, but do not modify any special chars or numerics
    const regex = /[a-zA-Z]/g;
    const origin = input.replace(regex, '');
    return origin.split('/');
  }

  getRegionalEcosystemFeaturesGroupedByVmStatId(vegREFeatureCollection: FeatureCollection<Polygon>): IReGroupByVmStatusIdAndFeatures[] {
    // VM_STAT_ID is a special property in the ESRI Veg RE map, it categorises Regional Ecosystem (RE) Id grouping
    let reVmstatusUniqueIdResultSet = [...new Set(vegREFeatureCollection.features.map((x) => x.properties).map((p) => (p ? (this.getPropCaseInsensitive(p, 'VM_STAT_ID') ?? null) : 0)))];
    if (reVmstatusUniqueIdResultSet) reVmstatusUniqueIdResultSet = reVmstatusUniqueIdResultSet.sort((a, b) => a - b);

    const reFeaturesGroupedById: IReGroupByVmStatusIdAndFeatures[] = [];

    reVmstatusUniqueIdResultSet.forEach((uniqueREVmStatId) => {
      const matchingFeatures = vegREFeatureCollection.features.filter((c) => this.getPropCaseInsensitive(c.properties, 'VM_STAT_ID') == uniqueREVmStatId);

      // since some RE's are known by mutiple vmStatId from (e.g. 1 and 2 = reCatAorBAreaEndangered)
      // For this rendering purpose we just want to merge the id together (if relevant) for ease of processing.
      // use the first index / identifier.

      let vmStatIdToAdd = uniqueREVmStatId;
      switch (uniqueREVmStatId) {
        case vmStatIdLookup.reCatAorBAreaEndangered_1:
        case vmStatIdLookup.reCatAorBAreaEndangered_2:
          vmStatIdToAdd = vmStatIdLookup.reCatAorBAreaEndangered_1;
          break;
        case vmStatIdLookup.reCatAorBAreaContainConcern_3:
        case vmStatIdLookup.reCatAorBAreaContainConcern_4:
          vmStatIdToAdd = vmStatIdLookup.reCatAorBAreaContainConcern_3;
          break;
        case vmStatIdLookup.reCatCorRAreaEndangered_11:
        case vmStatIdLookup.reCatCorRAreaEndangered_12:
          vmStatIdToAdd = vmStatIdLookup.reCatCorRAreaEndangered_11;
          break;
        case vmStatIdLookup.reCatCorRAreaContainConcern_13:
        case vmStatIdLookup.reCatCorRAreaContainConcern_14:
          vmStatIdToAdd = vmStatIdLookup.reCatCorRAreaContainConcern_13;
          break;
        case vmStatIdLookup.reWater_9:
        case vmStatIdLookup.reWater_19:
          vmStatIdToAdd = vmStatIdLookup.reWater_9;
          break;
      }

      // only add based on the id's once, we then merge the content of the features array
      let existingRE = reFeaturesGroupedById.find((x) => x.reVmStatId == vmStatIdToAdd.toString());
      if (existingRE) {
        const index = reFeaturesGroupedById.findIndex((x) => x.reVmStatId == existingRE!.reVmStatId);
        let mergedFeatures: any;

        // combine the two geojson features lists together
        if (reFeaturesGroupedById[index].features) {
          mergedFeatures = [...reFeaturesGroupedById[index].features, ...matchingFeatures];
        }
        if (mergedFeatures) reFeaturesGroupedById[index].features = mergedFeatures;
      } else {
        // add it normally to the list
        reFeaturesGroupedById.push({ reVmStatId: vmStatIdToAdd, features: matchingFeatures } as IReGroupByVmStatusIdAndFeatures);
      }
    });

    return reFeaturesGroupedById;
  }

  private areasAreOverlapping(area1: Feature<Geometry, GeoJsonProperties>, area2: Feature<Geometry, GeoJsonProperties>): boolean {
    const hasPartialOverlap = turf.booleanOverlap(area1, area2);

    // If the areas overlap each other completely, turf.booleanOverlap returns false.
    // We do not want to allow complete overlaps, so this checks for that case.
    if (!hasPartialOverlap) {
      const is1InsideOf2 = turf.difference(turf.featureCollection([area1 as Feature<Polygon>, area2 as Feature<Polygon>])) === null;
      const is2InsideOf1 = turf.difference(turf.featureCollection([area2 as Feature<Polygon>, area1 as Feature<Polygon>])) === null;

      return is1InsideOf2 || is2InsideOf1;
    }

    return hasPartialOverlap;
  }
  // Function to safely access property by case-insensitive name
  public getPropCaseInsensitive = (obj: any, propertyName: string) => {
    const lowerCasePropertyName = propertyName.toLowerCase();
    for (let key in obj) {
      if (key.toLowerCase() === lowerCasePropertyName) {
        return obj[key];
      }
    }
    return null; // Property not found
  };
}
